import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { DropResult } from 'react-beautiful-dnd';
import parse from 'utils/time/parse';
import { isoDatePattern } from 'utils/time/datePatterns';
import { useQueryClient } from '@tanstack/react-query';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { EVENTS_KEY } from './useCJSeances';
import { CJEvent } from 'types/event';
import { useUpdateCJSeance } from './useUpdateCJSeance';
import { useDeleteCJSeance } from './useDeleteCJSeance';
import { useCreateCJSeances } from './useCreateCJSeances';
import { logNetworkError } from 'utils/errors';
import differenceInMinutes from 'utils/time/differenceInMinutes';
import addMinutes from 'utils/time/addMinutes';
import setDateAtAnotherDate from 'utils/time/setDateAtAnotherDate';
import { getGroupedEvents } from '../utils/getGroupedEvents';
import { getGroupColumnTitle } from '../DayColumn/getGroupColumnTitle';
import useClassrooms from 'containers/App/context/ClassroomsContext/useClassrooms';
import { ClassroomsOrg } from 'types/school';
import { Classroom } from 'types/classroom';

function moveEventBeforeOrAfterDestinationEvent(
  event: CJEvent,
  otherEvent: CJEvent,
  position: 'before' | 'after',
  inheritClassrooms?: boolean,
) {
  const eventLength = differenceInMinutes(event.end, event.start);
  if (position === 'before') {
    return {
      ...event,
      start: addMinutes(otherEvent.start, -eventLength),
      end: otherEvent.start,
      classroomIds: inheritClassrooms
        ? otherEvent.classroomIds
        : event.classroomIds,
    };
  }
  return {
    ...event,
    start: otherEvent.end,
    end: addMinutes(otherEvent.end, eventLength),
    classroomIds: inheritClassrooms
      ? otherEvent.classroomIds
      : event.classroomIds,
  };
}

function moveEventToAnotherColumn(
  event: CJEvent,
  destinationClassroomIds: string[],
  destinationDate: Date,
) {
  // case where we move an event to another day, but classrooms are different on that day
  const compatibleClassrooms = event.classroomIds.filter(cl =>
    destinationClassroomIds.includes(cl),
  );
  return {
    ...event,
    start: setDateAtAnotherDate(event.start, destinationDate),
    end: setDateAtAnotherDate(event.end, destinationDate),
    classroomIds:
      compatibleClassrooms.length > 0
        ? compatibleClassrooms
        : destinationClassroomIds,
  };
}

/**
 * we rebuild the UI "state" and find the destination event to check if it is in a group
 * note for the future : we should store the structure with lines/groups/events (+title, lineStart, lineEnd...) instead of having CJEvent[][][]
 */
function isEventInAColumnWithTitle(
  destinationEventId: string,
  eventsOfDestination: CJEvent[],
  classrooms: Classroom[],
  classroomsOrg: ClassroomsOrg,
) {
  const destinationGroupedEvents = getGroupedEvents(eventsOfDestination);
  const destinationEventLine =
    destinationGroupedEvents.find(g =>
      g.some(ee => ee.some(e => e.id === destinationEventId)),
    ) || [];
  const destinationEventGroup = destinationEventLine.find(g =>
    g.some(e => e.id === destinationEventId),
  );
  const columnTitle = getGroupColumnTitle(
    destinationEventGroup || [],
    classrooms,
    classroomsOrg,
    destinationEventLine.length,
  );

  return !!columnTitle;
}

function parseDropResultIds(
  draggableId: string,
  destinationDroppableId: string,
) {
  const [draggedEventDayString, draggedEventClassroomId, draggedEventId] =
    draggableId.split('_');
  const [destinationDayString, destinationClassroomId, destinationEventId] =
    destinationDroppableId.split('_');

  const draggedEventDate = parse(draggedEventDayString, isoDatePattern);
  const destinationDate = parse(destinationDayString, isoDatePattern);

  return {
    draggedEventDayString,
    draggedEventClassroomId,
    draggedEventId,
    draggedEventDate,
    destinationDayString,
    destinationClassroomId,
    destinationEventId,
    destinationDate,
  };
}

export function useOnDragEnd(deps: string) {
  const queryClient = useQueryClient();
  const { schoolyear, school } = useSchoolSchoolyear();
  const classrooms = useClassrooms();
  const updateEvent = useUpdateCJSeance();
  const removeSeance = useDeleteCJSeance();
  const createSeance = useCreateCJSeances();

  return useCallback(
    (result: DropResult) => {
      const { destination, source, draggableId } = result;
      // draggableId and droppableId look like : `${dayString}_${classroom.id}_${event.id}`
      // we put the classroomId in it other for a multiclassroom event in split mode because 2 seances can have the same id, which is bad 👿
      if (
        destination?.droppableId === source?.droppableId &&
        destination?.index === source?.index
      ) {
        // event dropped at the same place, do nothing
        return Promise.resolve();
      }

      if (!destination || !destination.droppableId) {
        return Promise.resolve();
      }

      const {
        draggedEventDayString,
        draggedEventClassroomId,
        draggedEventId,
        draggedEventDate,
        destinationDayString,
        destinationClassroomId,
        destinationEventId,
        destinationDate,
      } = parseDropResultIds(draggableId, destination.droppableId);

      const eventsOfSource = queryClient.getQueryData<CJEvent[]>([
        schoolyear.id,
        school.id,
        EVENTS_KEY,
        draggedEventDayString,
      ]);
      const eventsOfDestination = queryClient.getQueryData<CJEvent[]>([
        schoolyear.id,
        school.id,
        EVENTS_KEY,
        destinationDayString,
      ]);

      const draggedEvent = (eventsOfSource || []).find(
        e => e.id === draggedEventId,
      );
      if (!draggedEvent) {
        return Promise.resolve();
      }

      if (destinationEventId === 'empty') {
        // event dropped on an empty column
        return updateEvent({
          event: moveEventToAnotherColumn(
            draggedEvent,
            destinationClassroomId.split('%%'),
            destinationDate,
          ),
          previousDay: draggedEventDate,
        });
      }

      const destinationEvent = (eventsOfDestination || []).find(
        e => e.id === destinationEventId,
      );

      if (!destinationEvent) {
        return Promise.resolve();
      }

      const inheritDestinationClassrooms = isEventInAColumnWithTitle(
        destinationEventId,
        eventsOfDestination || [],
        classrooms,
        school.classroomsOrg,
      );

      // event dropped near another event : update start/end depending on its position (before/after)
      // if the destination is in a column with a title, we inherit the classrooms from the destination event (the user might want to move the event to the group)
      const newEvent = moveEventBeforeOrAfterDestinationEvent(
        draggedEvent,
        destinationEvent,
        destination.index === 0 ? 'before' : 'after',
        inheritDestinationClassrooms,
      );

      if (
        school.classroomsOrg === 'split' &&
        draggedEvent.classroomIds.length > 1
      ) {
        return Promise.all([
          createSeance({
            cjEvents: [
              {
                ...newEvent,
                id: uuid(),
              },
            ],
          }).catch(err => {
            logNetworkError(err, "Couldn't create cj event when draging it");
          }),
          removeSeance({
            seance: draggedEvent,
            classroomId: draggedEventClassroomId,
          }),
        ]);
      }

      return updateEvent({
        event: newEvent,
        previousDay: draggedEventDate,
      });
    },
    [deps],
  );
}
